import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { fetchSignaturePreviewUrl } from '../../signature/signatureApi';
import GeneralProfileChatThread from './GeneralProfileChatThread';
import GeneralProfileChatComposer from './GeneralProfileChatComposer';
import { useGeneralProfileFlow } from './useGeneralProfileFlow';
import ContinueButtonBar from '../components/ContinueButtonBar';
import { useWheelLocale } from '../hooks/useWheelLocale';
import './GeneralProfileChatFlow.css';

export interface GeneralProfileNamePayload {
  familyName: string;
  firstName: string;
  middleName: string;
  familyNameKana: string;
  firstNameKana: string;
  middleNameKana: string;
  /** YYYY-MM-DD */
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other' | 'unknown';
  nationality: string;
  phoneNumber: string;
  homePhoneNumber: string;
  postalCode: string;
  addressPref: string;
  addressCity: string;
  addressTown: string;
  addressLine2: string;
  email: string;
  residenceCardNumber: string;
  myNumber: string;
  maritalStatus?: 'married' | 'not_married';
  isWidowed: boolean;
  isSingleParent: boolean;
  isWorkingStudent: boolean;
  disabilityCategory?: 'none' | 'general' | 'special';
  /** Free-text disability particulars (also stored as disabilityFactsJson). */
  disabilityFacts?: string;
  disabilityFactsJson?: string;
  emergencyContactName: string;
  emRelationship: string;
  emergencyContactPhoneNumber: string;
  emergencyContactPostalCode: string;
  emergencyContactAddressPref: string;
  emergencyContactAddressCity: string;
  emergencyContactAddressTown: string;
  emergencyContactAddressLine2: string;
}

export interface GeneralProfileChatFlowProps {
  onBack: () => void;
  /** After final review, open signature capture (profile stays at final_confirm). */
  onProceedToSignature: (data: GeneralProfileNamePayload) => void;
  /** After signature exists, mark general profile complete and leave this flow. */
  onComplete: (data: GeneralProfileNamePayload) => void;
  /** Usually the logged-in email; pre-fills confirmation step */
  currentEmail?: string | null;
  /** When re-opening from checklist Edit, pre-fill fields */
  initialValues?: Partial<GeneralProfileNamePayload> | null;
  /** When true, load signature image into chat history if available */
  hasSignature?: boolean;
  /** Open signature capture to replace the stored signature */
  onRedoSignature?: () => void;
}

const GeneralProfileChatFlow: React.FC<GeneralProfileChatFlowProps> = ({
  onBack,
  onProceedToSignature,
  onComplete,
  initialValues,
  currentEmail,
  hasSignature = false,
  onRedoSignature,
}) => {
  const { t } = useLanguage();
  const wheelLocale = useWheelLocale();
  const flow = useGeneralProfileFlow({
    initialValues,
    currentEmail,
    onComplete,
    onProceedToSignature,
    t,
    wheelLocale,
  });
  const [signatureImageUrl, setSignatureImageUrl] = useState<string | null>(null);
  const signatureUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasSignature || !currentEmail?.trim()) {
      if (signatureUrlRef.current) {
        URL.revokeObjectURL(signatureUrlRef.current);
        signatureUrlRef.current = null;
      }
      setSignatureImageUrl(null);
      return;
    }

    let cancelled = false;
    void fetchSignaturePreviewUrl(currentEmail).then((url) => {
      if (cancelled) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      if (signatureUrlRef.current) {
        URL.revokeObjectURL(signatureUrlRef.current);
      }
      signatureUrlRef.current = url;
      setSignatureImageUrl(url);
    });

    return () => {
      cancelled = true;
    };
  }, [currentEmail, hasSignature]);

  useEffect(() => {
    return () => {
      if (signatureUrlRef.current) {
        URL.revokeObjectURL(signatureUrlRef.current);
        signatureUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!signatureImageUrl) return;
    const id = window.setTimeout(() => {
      flow.threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
    return () => window.clearTimeout(id);
  }, [signatureImageUrl]);

  return (
    <div className="gen-profile-chat">
      <div className="gen-profile-chat__card">
        <header className="gen-profile-chat__top">
          <button type="button" className="gen-profile-chat__cancel-pill" onClick={onBack}>
            <span className="gen-profile-chat__cancel-x" aria-hidden>
              ×
            </span>
            {t('submitDocuments.chat.cancelProcedure')}
          </button>
        </header>

        <div className="gen-profile-chat__thread-wrap">
          <GeneralProfileChatThread
            t={t}
            introRevealed={flow.introRevealed}
            step={flow.step}
            threadEndRef={flow.threadEndRef}
            legalFamily={flow.legalFamily}
            legalFirst={flow.legalFirst}
            legalMiddle={flow.legalMiddle}
            kanaFamily={flow.kanaFamily}
            kanaFirst={flow.kanaFirst}
            kanaMiddle={flow.kanaMiddle}
            dobDisplayFormatted={flow.dobDisplayFormatted}
            genderLabel={flow.genderLabel}
            nationalityLabel={flow.nationalityLabel}
            phoneNumber={flow.phoneNumber}
            homePhoneDisplay={flow.homePhoneDisplay}
            address={flow.address}
            email={flow.email}
            residenceCardNumber={flow.residenceCardNumber}
            myNumber={flow.myNumber}
            myNumberDisplay={flow.myNumberDisplay}
            showResidenceCardStep={flow.showResidenceCardStep}
            maritalStatusLabel={flow.maritalStatusLabel}
            familyStatusDisplay={flow.familyStatusDisplay}
            disabilityCategoryLabel={flow.disabilityCategoryLabel}
            disabilityFacts={flow.disabilityFacts}
            emergencyHasContact={flow.emergencyHasContact}
            emergencyContactLabel={flow.emergencyContactLabel}
            emergencyName={flow.emergencyName}
            emRelationship={flow.emRelationship}
            emergencyPhone={flow.emergencyPhone}
            emergencyAddress={flow.emergencyAddress}
            legalCommitted={flow.legalCommitted}
            kanaCommitted={flow.kanaCommitted}
            dobCommitted={flow.dobCommitted}
            genderCommitted={flow.genderCommitted}
            nationalityCommitted={flow.nationalityCommitted}
            phoneCommitted={flow.phoneCommitted}
            homePhoneCommitted={flow.homePhoneCommitted}
            addressCommitted={flow.addressCommitted}
            emailCommitted={flow.emailCommitted}
            residenceCardCommitted={flow.residenceCardCommitted}
            myNumberCommitted={flow.myNumberCommitted}
            maritalStatusCommitted={flow.maritalStatusCommitted}
            familyStatusCommitted={flow.familyStatusCommitted}
            disabilityCategoryCommitted={flow.disabilityCategoryCommitted}
            disabilityFactsCommitted={flow.disabilityFactsCommitted}
            emergencyContactCommitted={flow.emergencyContactCommitted}
            emergencyNameCommitted={flow.emergencyNameCommitted}
            emRelationshipCommitted={flow.emRelationshipCommitted}
            emergencyPhoneCommitted={flow.emergencyPhoneCommitted}
            emergencyAddressCommitted={flow.emergencyAddressCommitted}
            onUndo={flow.handleUndo}
            onEdit={flow.beginEdit}
            showUndoLegal={flow.showUndoLegal}
            showUndoKana={flow.showUndoKana}
            showUndoDob={flow.showUndoDob}
            showUndoGender={flow.showUndoGender}
            showUndoNationality={flow.showUndoNationality}
            showUndoPhone={flow.showUndoPhone}
            showUndoHomePhone={flow.showUndoHomePhone}
            showUndoAddress={flow.showUndoAddress}
            showUndoEmail={flow.showUndoEmail}
            showUndoResidenceCard={flow.showUndoResidenceCard}
            showUndoMyNumber={flow.showUndoMyNumber}
            showUndoMaritalStatus={flow.showUndoMaritalStatus}
            showUndoFamilyStatus={flow.showUndoFamilyStatus}
            showUndoDisabilityCategory={flow.showUndoDisabilityCategory}
            showUndoDisabilityFacts={flow.showUndoDisabilityFacts}
            showUndoEmergencyContact={flow.showUndoEmergencyContact}
            showUndoEmergencyName={flow.showUndoEmergencyName}
            showUndoEmergencyRelationship={flow.showUndoEmergencyRelationship}
            showUndoEmergencyPhone={flow.showUndoEmergencyPhone}
            showUndoEmergencyAddress={flow.showUndoEmergencyAddress}
            signatureImageUrl={signatureImageUrl}
            onRedoSignature={onRedoSignature}
          />
        </div>

        <footer className="gen-profile-chat__composer">
          {flow.step === 'finalConfirm' ? (
            hasSignature ? (
              <ContinueButtonBar onContinue={flow.handleContinueAfterFinalConfirm} />
            ) : (
              <ContinueButtonBar
                onContinue={flow.handleProceedToSignature}
                label={t('submitDocuments.profileName.proceedToSign')}
              />
            )
          ) : (
            <GeneralProfileChatComposer
              t={t}
              wheelLocale={wheelLocale}
              step={flow.step}
              legalFamily={flow.legalFamily}
              legalFirst={flow.legalFirst}
              legalMiddle={flow.legalMiddle}
              kanaFamily={flow.kanaFamily}
              kanaFirst={flow.kanaFirst}
              kanaMiddle={flow.kanaMiddle}
              dob={flow.dob}
              gender={flow.gender}
              nationality={flow.nationality}
              phoneNumber={flow.phoneNumber}
              homePhoneNumber={flow.homePhoneNumber}
              homePhoneSkipped={flow.homePhoneSkipped}
              address={flow.address}
              email={flow.email}
              residenceCardNumber={flow.residenceCardNumber}
              myNumber={flow.myNumber}
              myNumberSkipped={flow.myNumberSkipped}
              maritalStatus={flow.maritalStatus}
              familyStatusSkipped={flow.familyStatusSkipped}
              familyStatusSelections={flow.familyStatusSelections}
              disabilityCategory={flow.disabilityCategory}
              disabilityFacts={flow.disabilityFacts}
              showWidowedFamilyOption={flow.showWidowedFamilyOption}
              emergencyHasContact={flow.emergencyHasContact}
              emergencyName={flow.emergencyName}
              emRelationship={flow.emRelationship}
              emergencyPhone={flow.emergencyPhone}
              emergencyAddress={flow.emergencyAddress}
              setLegalFamily={flow.setLegalFamily}
              setLegalFirst={flow.setLegalFirst}
              setLegalMiddle={flow.setLegalMiddle}
              setKanaFamily={flow.setKanaFamily}
              setKanaFirst={flow.setKanaFirst}
              setKanaMiddle={flow.setKanaMiddle}
              setDob={flow.setDob}
              setGender={flow.setGender}
              setNationality={flow.setNationality}
              setPhoneNumber={flow.setPhoneNumber}
              setHomePhoneNumber={flow.setHomePhoneNumber}
              setHomePhoneSkipped={flow.setHomePhoneSkipped}
              setAddress={flow.setAddress}
              setEmail={flow.setEmail}
              setResidenceCardNumber={flow.setResidenceCardNumber}
              setMyNumber={flow.setMyNumber}
              setMyNumberSkipped={flow.setMyNumberSkipped}
              setMaritalStatus={flow.setMaritalStatus}
              setFamilyStatusSkipped={flow.setFamilyStatusSkipped}
              setFamilyStatusSelections={flow.setFamilyStatusSelections}
              setDisabilityCategory={flow.setDisabilityCategory}
              setDisabilityFacts={flow.setDisabilityFacts}
              setEmergencyHasContact={flow.setEmergencyHasContact}
              setEmergencyName={flow.setEmergencyName}
              setEmRelationship={flow.setEmRelationship}
              setEmergencyPhone={flow.setEmergencyPhone}
              setEmergencyAddress={flow.setEmergencyAddress}
              validateLegalName={flow.validateLegalName}
              validateKana={flow.validateKana}
              validatePhone={flow.validatePhone}
              validateEmail={flow.validateEmail}
              validateResidenceCardNumber={flow.validateResidenceCardNumber}
              validateMyNumber={flow.validateMyNumber}
              canSendLegal={flow.canSendLegal}
              canSendKana={flow.canSendKana}
              canSendGender={flow.canSendGender}
              canSendPhone={flow.canSendPhone}
              canSendHomePhone={flow.canSendHomePhone}
              canSendAddress={flow.canSendAddress}
              canSendEmail={flow.canSendEmail}
              canSendResidenceCard={flow.canSendResidenceCard}
              canSendMyNumber={flow.canSendMyNumber}
              canSendMaritalStatus={flow.canSendMaritalStatus}
              canSendFamilyStatus={flow.canSendFamilyStatus}
              canSendDisabilityCategory={flow.canSendDisabilityCategory}
              canSendDisabilityFacts={flow.canSendDisabilityFacts}
              canSendEmergencyContact={flow.canSendEmergencyContact}
              canSendEmergencyName={flow.canSendEmergencyName}
              canSendEmergencyRelationship={flow.canSendEmergencyRelationship}
              canSendEmergencyPhone={flow.canSendEmergencyPhone}
              canSendEmergencyAddress={flow.canSendEmergencyAddress}
              handleSendLegal={flow.handleSendLegal}
              handleSendKana={flow.handleSendKana}
              handleSendDob={flow.handleSendDob}
              handleSendGender={flow.handleSendGender}
              handleSendNationality={flow.handleSendNationality}
              handleSendPhone={flow.handleSendPhone}
              handleSendHomePhone={flow.handleSendHomePhone}
              handleSkipHomePhone={flow.handleSkipHomePhone}
              handleSendAddress={flow.handleSendAddress}
              handleSendEmail={flow.handleSendEmail}
              handleSendResidenceCard={flow.handleSendResidenceCard}
              handleSendMyNumber={flow.handleSendMyNumber}
              handleSkipMyNumber={flow.handleSkipMyNumber}
              handleSendMaritalStatus={flow.handleSendMaritalStatus}
              handleSendFamilyStatus={flow.handleSendFamilyStatus}
              handleSendDisabilityCategory={flow.handleSendDisabilityCategory}
              handleSendDisabilityFacts={flow.handleSendDisabilityFacts}
              handleSendEmergencyContact={flow.handleSendEmergencyContact}
              handleSendEmergencyName={flow.handleSendEmergencyName}
              handleSendEmergencyRelationship={flow.handleSendEmergencyRelationship}
              handleSendEmergencyPhone={flow.handleSendEmergencyPhone}
              handleSendEmergencyAddress={flow.handleSendEmergencyAddress}
              nationalityOptions={flow.nationalityOptions}
            />
          )}
        </footer>
      </div>
    </div>
  );
};

export default GeneralProfileChatFlow;

